/**
 * Reporting Core SDK Types
 *
 * Framework-agnostic type definitions for the Reporting API.
 */

// ============================================================================
// Auth Provider
// ============================================================================

/**
 * Auth provider interface for injecting authentication.
 * Host applications implement this to provide tokens/headers.
 */
export interface AuthProvider {
  /**
   * Returns the current bearer token.
   * Called before each API request.
   */
  getAccessToken: () => Promise<string> | string;
}

/**
 * Alternative auth provider that returns full headers.
 */
export interface HeaderAuthProvider {
  /**
   * Returns headers to include in API requests.
   */
  getAuthHeaders: () => Promise<Record<string, string>> | Record<string, string>;
}

/**
 * Combined auth provider type - either token-based or header-based.
 */
export type AuthProviderConfig = AuthProvider | HeaderAuthProvider;

// ============================================================================
// SDK Configuration
// ============================================================================

/**
 * SDK client configuration options.
 */
export interface ReportingClientConfig {
  /**
   * Base URL for the Reporting API.
   * @default 'http://localhost:4000/api/v1'
   */
  baseUrl?: string;

  /**
   * Auth provider for authenticated requests.
   */
  authProvider: AuthProviderConfig;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Standard success response wrapper from the API.
 */
export interface SuccessResponse<T> {
  success: true;
  data: T;
  timestamp: string;
}

/**
 * Standard error response wrapper from the API.
 */
export interface ErrorResponse {
  success: false;
  error: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

/**
 * Combined API response type.
 */
export type ApiResponse<T> = SuccessResponse<T> | ErrorResponse;

// ============================================================================
// Report Types
// ============================================================================

/**
 * Report summary returned by list endpoint.
 */
export interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
}

/**
 * List reports response data.
 */
export interface ListReportsData {
  reports: Report[];
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Filter option for select/multiSelect filters.
 */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Options source configuration for a filter.
 */
export interface OptionsSource {
  type: 'static' | 'query';
  queryId?: string;
}

/**
 * Filter type identifiers.
 */
export type FilterType = 'date' | 'select' | 'multiSelect' | 'text' | 'number';

/**
 * Filter definition from report config.
 */
export interface Filter {
  id: string;
  name: string;
  type: FilterType;
  required: boolean;
  defaultValue: unknown;
  options?: FilterOption[];
  optionsSource?: OptionsSource;
  hidden?: boolean;
  source?: 'user' | 'system';
}

/**
 * Report configuration with filters.
 */
export interface ReportConfig {
  reportId: string;
  filters: Filter[];
}

/**
 * Filter options response data.
 */
export interface FilterOptionsData {
  filterId: string;
  options: FilterOption[];
}

// ============================================================================
// Report Data Types
// ============================================================================

/**
 * Column type identifiers.
 */
export type ColumnType = 'string' | 'number' | 'date' | 'boolean';

/**
 * Column definition in report data.
 */
export interface Column {
  id: string;
  label: string;
  type: ColumnType;
}

/**
 * Row data - dynamic key-value pairs.
 */
export type Row = Record<string, unknown>;

/**
 * Pagination metadata.
 */
export interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
}

/**
 * Query result set from a single query in a multi-query report.
 *
 * Contains rows, column metadata, and optional pagination for
 * paginatable queries.
 */
export interface QueryResultSet {
  /** Data rows returned by the query */
  rows: Row[];

  /** Column definitions with names */
  columns: Array<{ name: string }>;

  /** Number of rows returned */
  rowCount: number;

  /**
   * Pagination metadata for this query result.
   *
   * Only present when the query was executed with pagination.
   * Summary queries (non-paginatable) will not have this field.
   */
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Multi-query result set.
 *
 * For multi-query reports, contains all query results keyed by
 * their output names (e.g., 'summary', 'details').
 */
export interface MultiQueryResult {
  [outputName: string]: QueryResultSet;
}

/**
 * Report data returned by generate endpoint.
 */
export interface ReportData {
  reportId: string;
  generatedAt: string;
  data: {
    columns: Column[];
    rows: Row[];
  };
  pagination: PaginationData;

  /**
   * Multi-query results for reports with multiple queries.
   *
   * For multi-query reports, this contains all query results keyed
   * by their output names (e.g., 'summary', 'details').
   *
   * When present, the `data` field contains the main query result,
   * while `queries` provides access to all query results including
   * sub-queries with their own pagination.
   *
   * @example
   * ```ts
   * if (reportData.queries) {
   *   // Multi-query report
   *   const summary = reportData.queries.summary;
   *   const details = reportData.queries.details;
   *   console.log(`Summary: ${summary.rowCount} rows`);
   *   console.log(`Details: ${details.pagination?.totalRecords} total`);
   * }
   * ```
   */
  queries?: MultiQueryResult;
}

/**
 * Export format type.
 */
export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

/**
 * Report format type (includes display and export formats).
 */
export type ReportFormat = 'html' | ExportFormat;

/**
 * Export metadata with download URL.
 */
export interface ExportMetadata {
  downloadUrl: string;
  filename?: string;
  expiresAt?: string;
  format?: ExportFormat;
  fileSize?: number;
  reportId?: string;
  generatedAt?: string;
}

/**
 * HTML response with pagination metadata from headers.
 */
export interface HtmlResponse {
  /** Rendered HTML content */
  html: string;

  /**
   * Pagination metadata extracted from HTTP headers.
   * Available when the API includes X-Report-* pagination headers.
   */
  pagination?: PaginationData;
}

/**
 * Execution mode for report generation.
 */
export type ExecutionMode = 'sync' | 'async';

/**
 * Generate report request parameters.
 */
export interface GenerateReportRequest {
  filters: Record<string, unknown>;
  format: ReportFormat;
  page?: number;
  pageSize?: number;
  renderHtml?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /**
   * Execution mode for report generation.
   * - 'sync' (default): Execute synchronously and return data directly.
   * - 'async': Execute asynchronously. Returns 202 Accepted with run metadata.
   */
  executionMode?: ExecutionMode;
}

// ============================================================================
// Async Report Run Types
// ============================================================================

/**
 * Report run status values.
 */
export type ReportRunStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/**
 * Error details for failed runs.
 */
export interface ReportRunError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Report run metadata returned from async generate and polling endpoints.
 */
export interface ReportRunMetadata {
  /** Unique run identifier */
  runId: string;

  /** ID of the report being executed */
  reportId: string;

  /** Current run status */
  status: ReportRunStatus;

  /** User-friendly status message */
  statusMessage?: string;

  /** When the run was created */
  createdAt: string;

  /** When the run was last updated */
  updatedAt: string;

  /** When the run expires */
  expiresAt: string;

  /** URL to poll for status updates (relative path) */
  pollUrl: string;

  /** Download URL for export results (when succeeded) */
  downloadUrl?: string;

  /** URL to fetch JSON/HTML result (when succeeded) */
  resultUrl?: string;

  /** Error details (when failed) */
  error?: ReportRunError;
}

/**
 * Options for polling an async run until completion.
 */
export interface PollOptions {
  /**
   * Interval between polls in milliseconds.
   * @default 2000
   */
  pollIntervalMs?: number;

  /**
   * Maximum time to wait for completion in milliseconds.
   * @default 600000 (10 minutes)
   */
  maxWaitMs?: number;

  /**
   * Callback invoked on each status update.
   */
  onStatusChange?: (run: ReportRunMetadata) => void;
}

// ============================================================================
// SDK Error Types
// ============================================================================

/**
 * SDK error with structured details.
 */
export class ReportingApiError extends Error {
  /** HTTP status code when available */
  readonly statusCode?: number;

  /** API error code from the error response */
  readonly errorCode?: string;

  /** Additional error details */
  readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    options?: {
      statusCode?: number;
      errorCode?: string;
      details?: Record<string, unknown>;
    }
  ) {
    super(message);
    this.name = 'ReportingApiError';
    this.statusCode = options?.statusCode;
    this.errorCode = options?.errorCode;
    this.details = options?.details;
  }

  /**
   * Check if the error is an unauthorized error.
   */
  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if the error is a forbidden error.
   */
  isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /**
   * Check if the error is a not found error.
   */
  isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /**
   * Check if the error is a validation error.
   */
  isValidationError(): boolean {
    return this.statusCode === 400 || this.errorCode === 'VALIDATION_ERROR';
  }
}

// ============================================================================
// Hook State Types
// ============================================================================

/**
 * Generic async state for hooks.
 */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ReportingApiError | null;
}

/**
 * Filter values state - maps filter IDs to their values.
 */
export type FilterValues = Record<string, unknown>;

/**
 * Options cache key.
 */
export type OptionsCacheKey = string;

/**
 * Options cache entry.
 */
export interface OptionsCacheEntry {
  options: FilterOption[];
  fetchedAt: number;
}
